import {
    buildOptionalParams,
    buildPath,
    buildTemplatePath,
    CrudMode,
    Dialog,
    handleClose,
    PageRoute,
    ScreenType,
    useParams,
    useUnsavedChanges,
    useValidationErrors,
} from "@compass/core";
import {
    ScholarshipType,
    ScholarshipTypeEditResource,
    ScholarshipTypeViewResource,
    Permission
} from "generated/openApi";
import { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { ScholarshipTypePageHeader } from "./ScholarshipTypePageHeader";
import { ScholarshipTypePageBody } from "./ScholarshipTypePageBody";

const routeName = "scholarshipType";

class Params {
    id: number = 0;
    crudMode: CrudMode = "view";
    screenType: ScreenType = "standard";
}

class OptionalParams {
    closeOnSave?: boolean = false;
}

export const scholarshipTypePageRoute: PageRoute<Params, OptionalParams> = {
    handle: "Scholarship Type",
    templatePath: buildTemplatePath(routeName, Params),
    getPath: (params) => buildPath(routeName, params),
    getOptionalParams: (params) => buildOptionalParams(params),
};

export type ScholarshipTypeContextValue = {
    onCreate?: (id: number) => void;
};

export const ScholarshipTypeContext = createContext<ScholarshipTypeContextValue>({} as ScholarshipTypeContextValue);

export const ScholarshipTypePage = () => {
    const ROOT_PERMISSION: Permission = Permission.SystemUtility;
    const navigate = useNavigate();
    const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
    const [scholarshipType, setScholarshipType] = useState<ScholarshipType>({} as ScholarshipType);
    const [initScholarshipType, setInitScholarshipType] = useState<ScholarshipType>(scholarshipType);
    const [viewResource, setViewResource] = useState<ScholarshipTypeViewResource>({});
    const [editResource, setEditResource] = useState<ScholarshipTypeEditResource>({});
    const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [scholarshipType] });
    const { validationErrors, setValidationErrors } = useValidationErrors();
    const [closeOnSave] = useState<boolean>(optionalParams.closeOnSave || false);

    useEffect(() => {
        fetch(params.id);

        ApiService.scholarshipTypes.getViewResource().then((response) => {
            setViewResource(response);
        });
    }, []);

    useEffect(() => {
        if (params.crudMode === "edit") {
            ApiService.scholarshipTypes.getEditResource().then((response) => {
                setEditResource(response);
            });
        }
    }, [params.crudMode]);

    const reroute = (id: number, crudMode: CrudMode) =>
        navigate(`../${scholarshipTypePageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType, })}`);

    const fetch = (id: number) => {
        if (id > 0) {
            ApiService.scholarshipTypes.get(id).then((response) => {
                setScholarshipType(response);
                setInitScholarshipType(response);
                resetUnsavedChanges([response]);
            });
        } else {
            // For new
            setScholarshipType({} as ScholarshipType);
            setInitScholarshipType({} as ScholarshipType);
            resetUnsavedChanges([{} as ScholarshipType]);
        }
    };

    const pageHeader = (
        <ScholarshipTypePageHeader
            rootPermission={ROOT_PERMISSION}
            handle={scholarshipTypePageRoute.handle}
            crudMode={params.crudMode}
            reroute={reroute}
            fetch={fetch}
            scholarshipType={scholarshipType}
            setScholarshipType={setScholarshipType}
            initScholarshipType={initScholarshipType}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
            resetUnsavedChanges={resetUnsavedChanges}
            closeOnSave={closeOnSave}
        />
    );

    const pageBody = (
        <ScholarshipTypePageBody
            scholarshipType={scholarshipType}
            setScholarshipType={setScholarshipType}
            viewResource={viewResource}
            editResource={editResource}
            crudMode={params.crudMode}
            validationErrors={validationErrors}
        />
    );

    return (
        <Dialog
            isForm
            open={true}
            onClose={() => handleClose(navigate, unsavedChanges)}
            maxWidth="md"
            header={pageHeader}
            content={pageBody}
        />
    );
};